import React from 'react';
import { BundleIssue } from '../../api/bundle';

// 套件创建向导的通用壳 (2026-09-16, 设计 doc/relate-import/07 §7).
// 顶栏 (标题 + 进度 + 步进按钮) + 左步骤条 + 中栏内容 + 右概览; 材料向导先用, 设备向导 (P2) 同壳复用 ——
// 所以这里只认"步骤/计数/issue"这些抽象, 不认识材料或设备的任何字段.
//
// 布局要点: **三栏各自滚动, 顶栏不参与滚动**. 一开始是整页一个滚动区 + 顶栏 sticky,
// 结果每让一个元素吸顶, 它上面那个就被埋掉 (tab 埋了修 tab, 草稿横幅又被埋) ——
// 那是在打地鼠. 改成页面整体定高、中栏在自己盒子里滚, 顶栏和两侧根本不在滚动流里,
// 遮挡这件事就不存在了.

export interface WizardStep {
    key: string;
    label: string;
    optional: boolean;
    /** 步骤条上的计数摘要, 如 "2/3 组" / "5 行"; 空串不显示 */
    badge?: string;
    /** 该步是否有阻断错误 (步骤条打红点) */
    hasError?: boolean;
}

export interface SummaryItem {
    label: string;
    value: React.ReactNode;
}

interface Props {
    title: string;
    /** 顶部返回链接文案与回调 */
    backLabel: string;
    onBack: () => void;
    steps: WizardStep[];
    current: string;
    onStep: (key: string) => void;
    /** 概览栏条目 (右侧 sticky) */
    summary: SummaryItem[];
    /** 概览栏下方的预检计数区 */
    checkPanel?: React.ReactNode;
    /** 顶部右侧的额外操作 (如"用文件导入") */
    actions?: React.ReactNode;
    /** 步进操作 (上一步 / 跳过 / 下一步 / 提交) —— 与标题同一行, 随页面吸顶 */
    nav: React.ReactNode;
    children: React.ReactNode;
}

function StepDot({ s, active }: { s: WizardStep; active: boolean }) {
    // 有错优先于当前态: 红点要在回到别的步骤后依然看得见
    if (s.hasError) return <span className="text-rose-500">●</span>;
    if (active) return <span className="text-brand-600">●</span>;
    return <span className="text-slate-300">○</span>;
}

function WizardShell({
    title,
    backLabel,
    onBack,
    steps,
    current,
    onStep,
    summary,
    checkPanel,
    actions,
    nav,
    children,
}: Props) {
    const idx = steps.findIndex((s) => s.key === current);
    return (
        // h-full: 撑满外层滚动区, 让内部三栏各自滚而不是整页滚.
        // 不用负 margin 去抵外层的 p-6 —— 那会让总高多出 48px, 外层反而又滚起来.
        <div className="h-full flex flex-col gap-3">
            {/* 顶栏: 返回 / 标题 / 进度 / 额外操作 / 步进按钮 一行放齐, 常驻不滚动 */}
            <div className="card shrink-0 px-3 py-2 flex items-center gap-3">
                <button type="button" className="btn-ghost" onClick={onBack}>
                    ← {backLabel}
                </button>
                <h1 className="text-base font-semibold">{title}</h1>
                {idx >= 0 && (
                    <span className="text-xs text-slate-400">
                        第 {idx + 1} / {steps.length} 步 · {steps[idx].label}
                    </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                    {actions}
                    {actions && <span className="w-px h-5 bg-slate-200" />}
                    {nav}
                </div>
            </div>

            <div className="flex-1 min-h-0 flex gap-4 items-stretch">
                {/* 左: 步骤条 (自己滚, 不跟中栏联动) */}
                <nav className="card p-2 w-44 shrink-0 overflow-auto space-y-0.5 self-start max-h-full">
                    {steps.map((s, i) => {
                        const active = s.key === current;
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={() => onStep(s.key)}
                                className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-start gap-1.5 ${
                                    active
                                        ? 'bg-brand-50 text-brand-700 font-medium'
                                        : 'hover:bg-slate-50 text-slate-600'
                                }`}
                            >
                                <StepDot s={s} active={active} />
                                <span className="flex-1 min-w-0">
                                    <span className="block">
                                        {String(i + 1)}. {s.label}
                                    </span>
                                    <span className="block text-[11px] text-slate-400 font-normal truncate">
                                        {s.optional ? '可选' : ''}
                                        {s.optional && s.badge ? ' · ' : ''}
                                        {s.badge || ''}
                                    </span>
                                </span>
                            </button>
                        );
                    })}
                </nav>

                {/* 中: 内容 —— 唯一的滚动区, 滚多长都不会钻到顶栏或两侧底下 */}
                <div className="flex-1 min-w-0 overflow-auto space-y-3 pr-1">{children}</div>

                {/* 右: 概览 + 预检 (自己滚) */}
                <aside className="card p-3 w-56 shrink-0 overflow-auto space-y-3 self-start max-h-full">
                    <div>
                        <div className="text-xs font-semibold text-slate-600 mb-1.5">
                            套件概览
                        </div>
                        <div className="space-y-1">
                            {summary.map((s) => (
                                <div
                                    key={s.label}
                                    className="flex justify-between gap-2 text-[11px]"
                                >
                                    <span className="text-slate-400 shrink-0">{s.label}</span>
                                    <span className="text-slate-700 text-right break-all">
                                        {s.value}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {checkPanel}
                </aside>
            </div>

        </div>
    );
}

export default WizardShell;

/** IssueRow 单条预检结果 (错误/告警/提示同一套版式, 只换颜色). */
export function IssueRow({
    issue,
    onGoto,
}: {
    issue: BundleIssue;
    onGoto?: (issue: BundleIssue) => void;
}) {
    const tone =
        issue.severity === 'error'
            ? 'border-rose-200 bg-rose-50 text-rose-700'
            : issue.severity === 'warning'
              ? 'border-amber-200 bg-amber-50 text-amber-700'
              : 'border-slate-200 bg-slate-50 text-slate-600';
    return (
        <div className={`rounded border px-2 py-1.5 text-xs ${tone}`}>
            <div className="flex items-start gap-2">
                <span className="font-mono text-[10px] opacity-70 shrink-0">
                    {issue.code}
                </span>
                <span className="flex-1 min-w-0">{issue.msg}</span>
                {onGoto && (
                    <button
                        type="button"
                        className="text-[11px] underline shrink-0"
                        onClick={() => onGoto(issue)}
                    >
                        去修改
                    </button>
                )}
            </div>
            {issue.path && (
                <div className="font-mono text-[10px] opacity-60 mt-0.5">{issue.path}</div>
            )}
            {!!issue.candidates?.length && (
                <div className="flex flex-wrap gap-1 mt-1">
                    {issue.candidates.slice(0, 12).map((c) => (
                        <span
                            key={c}
                            className="tag bg-white/70 font-mono cursor-pointer"
                            title="点击复制"
                            onClick={() => navigator.clipboard?.writeText(c)}
                        >
                            {c}
                        </span>
                    ))}
                    {issue.candidates.length > 12 && (
                        <span className="text-[10px] opacity-60">
                            等 {issue.candidates.length} 项
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

/** FieldIssue 贴在表单控件下方的单条提示 (向导把 dryRun 结果按 path 回贴到字段). */
export function FieldIssue({ issue }: { issue?: BundleIssue }) {
    if (!issue) return null;
    const cls =
        issue.severity === 'error'
            ? 'text-rose-600'
            : issue.severity === 'warning'
              ? 'text-amber-600'
              : 'text-slate-400';
    return <div className={`text-[11px] mt-1 ${cls}`}>{issue.msg}</div>;
}
